import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

/**
 * Hook for fetching academic years from the backend
 */
const useAcademicYears = () => {
  const navigate = useNavigate();
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYearChoices, setAcademicYearChoices] = useState([]);
  const [currentAcademicYear, setCurrentAcademicYear] = useState(null);
  const [loading, setLoading] = useState(false);

  /**
   * Fetch all academic years from database
   */
  const fetchAcademicYears = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.enrollment.academicYears,
        null,
        navigate,
      );

      if (response.ok) {
        setAcademicYears(response.data || []);
      } else {
        Utilities.showError(response, "Failed to fetch academic years");
      }
    } catch (error) {
      console.error("Error fetching academic years:", error);
      message.error("An error occurred while fetching academic years");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  /**
   * Fetch academic year choices (from ACADEMIC_YEAR_CHOICES)
   */
  const fetchAcademicYearChoices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.enrollment.academicYearsChoices,
        null,
        navigate,
      );

      if (response.ok) {
        setAcademicYearChoices(response.data || []);
      } else {
        Utilities.showError(response, "Failed to fetch academic year choices");
      }
    } catch (error) {
      console.error("Error fetching academic year choices:", error);
      message.error("An error occurred while fetching academic year choices");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  /**
   * Fetch current academic year
   */
  const fetchCurrentAcademicYear = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.enrollment.currentAcademicYear,
        null,
        navigate,
      );

      if (response.ok) {
        setCurrentAcademicYear(response.data);
      } else {
        console.warn("No current academic year set");
      }
    } catch (error) {
      console.warn("Error fetching current academic year:", error);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  return {
    academicYears,
    academicYearChoices,
    currentAcademicYear,
    loading,
    fetchAcademicYears,
    fetchAcademicYearChoices,
    fetchCurrentAcademicYear,
  };
};

export default useAcademicYears;
